import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; 
import { useContext } from "react";
import { AuthContext } from "../Context/AuthContext";
import { getApiUrl } from "../config/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/Components/ui/card";
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { Label } from "@/Components/ui/label";
import { Stack } from "@/Components/ui/layout";
import { Text } from "@/Components/ui/typography";
import { cn } from "@/lib/utils";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate(); 
  const { setIsAuthenticated } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setErrorMessage(""); 

    try {
      const response = await fetch(getApiUrl("/api/employer-login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email, password }, { withCredentials: true }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const data = await response.json();
      // Debug log removed — was leaking login response (incl. token) to console.

      localStorage.setItem("token", data.token);
      setIsAuthenticated(true);
      navigate("/jobpage");
    } catch (error) {
      console.error("Error during login:", error.message);
      setErrorMessage(error.message);
    }
  };

  return (
    <Card className="rounded-[25px] shadow-md border-[5px] border-muted p-5 mt-7.5">
      <CardHeader className="p-0 pb-0">
        <CardTitle className="text-[30px] text-foreground font-poppins m-0">Login</CardTitle>
        <CardDescription className="text-muted-foreground font-poppins mt-2.5 mb-10">
          Explore our world
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-0">
        <form onSubmit={handleSubmit} className="w-full max-w-[495px]">
          {errorMessage && (
            <Text size="sm" variant="destructive" className="mb-4">
              {errorMessage}
            </Text>
          )}

          <Stack spacing="lg" className="mb-6">
            <Stack spacing="sm">
              <Label htmlFor="email" className="sr-only">Email</Label>
              <Input
                type="email"
                id="gmail"
                placeholder="Enter your email*"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-[18px] px-4 py-5.5 text-lg border-foreground"
              />
            </Stack>

            <Stack spacing="sm">
              <Label htmlFor="password" className="sr-only">Password</Label>
              <div className="relative">
                <Input
                  type={passwordVisible ? "text" : "password"}
                  id="password"
                  placeholder="Enter your password*"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-[18px] px-4 py-5.5 text-lg border-foreground pr-12"
                />
                <img
                  src={
                    !passwordVisible
                      ? "https://cdn-icons-png.flaticon.com/128/709/709612.png"
                      : "https://cdn-icons-png.flaticon.com/128/2767/2767146.png"
                  }
                  alt="Toggle password visibility"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5.5 h-5.5 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
              </div>
            </Stack>
          </Stack>

          <Stack spacing="sm">
            <Button 
              type="submit" 
              variant="default"
              className="w-[161px] text-lg font-poppins tracking-wide py-2.5 px-2.5 bg-gradient-to-r from-primary via-[#50c598] to-primary"
            >
              Continue
            </Button>
            <Text size="sm" className="mt-4 text-primary font-poppins">
              Don't have an account? <a href="/signup" className="underline">Sign Up</a>
            </Text>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
}

export default LoginForm;
